import { Component, Input, OnInit } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Destroyable, Rental, selectSome, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { InquiryChargeService } from '@tv3/services/inquiry-charge.service'
import { InquiryService } from '@tv3/store/inquiry/inquiry.service'
import { LoadInquiryDetails } from '@tv3/store/inquiry/inquiry.actions'
import { Inquiry, InquiryPaymentStatus } from '@tv3/store/inquiry/inquiry.model'
import { selectInquiriesByIds } from '@tv3/store/inquiry/inquiry.selectors'
import { map, switchMap } from 'rxjs/operators'
import { InquiryCost } from '@tokeet/cost-resolver'
import { combineLatest, of, BehaviorSubject, from } from 'rxjs'
import { NgxPermissionsService } from 'ngx-permissions'
import { AuthService } from '@tv3/services/auth.service'

@Component({
  selector: 'app-booking-tooltip-popup',
  templateUrl: './booking-tooltip-popup.component.html',
  styleUrls: ['./booking-tooltip-popup.component.scss'],
})
export class BookingTooltipPopupComponent extends Destroyable implements OnInit {
  @Input() bookingId: string
  @Input() rental: Rental

  booking: Inquiry
  charges: InquiryCost
  formulaAdjustedCost: number
  paymentStatus: { paid: boolean; remaining: number; status: InquiryPaymentStatus }
  isBookingLoading = true
  isChargeLoading = true

  get currentUser() {
    return this.auth.user
  }
  get isOwner() {
    return this.auth.hasRole('owner')
  }

  canSeeCharges$ = new BehaviorSubject(false)

  hasPermission$(permissions: NgxPermissionsService, per: string | string[]) {
    return from(permissions.hasPermission(per))
  }

  getBookingFormulaTooltip() {
    return (
      $localize`Booking costs were adjusted via the booking formula set on the '` +
      this.booking?.inquirySource +
      $localize`' Channel Details settings`
    )
  }

  constructor(
    private store: Store<any>,
    private auth: AuthService,
    private inquiryChargeService: InquiryChargeService,
    private inquiryService: InquiryService,
    private permissions: NgxPermissionsService
  ) {
    super()
  }

  ngOnInit(): void {
    this.store.dispatch(LoadInquiryDetails({ id: this.bookingId }))
    const booking$ = this.store.pipe(
      selectSome(selectInquiriesByIds([this.bookingId])),
      map(([booking]) => booking)
    )
    booking$.pipe(untilDestroy(this)).subscribe((booking) => {
      this.booking = booking
      this.isBookingLoading = !booking
    })
    booking$
      .pipe(
        switchMap((booking) =>
          combineLatest([
            this.inquiryChargeService.getCharges(booking),
            this.inquiryChargeService.getBookingPaymentStatusForInquiry(booking),
            this.inquiryService.getCostAfterFormula(booking.id),
          ])
        ),
        untilDestroy(this)
      )
      .subscribe(([charges, status, formulaAdjustedCost]) => {
        this.charges = charges
        this.paymentStatus = status
        this.formulaAdjustedCost = formulaAdjustedCost
        this.isChargeLoading = !charges || !status
      })

    const canSeeCharges$ = () => {
      if (this.isOwner) {
        return of(!!this.currentUser?.settings?.total)
      } else {
        return this.hasPermission$(this.permissions, 'canEditInquiry')
      }
    }
    canSeeCharges$().subscribe((canSeeCharges) => {
      this.canSeeCharges$.next(canSeeCharges)
    })
  }
}
